import React, { useEffect, useMemo, useState } from "react";
import { X, Plus, Star, ChevronDown } from "lucide-react";
import { supabase } from "../../supabase/supabase";
import { useAuth } from "../../context/AuthContext";
import { useLeague } from "../../context/LeagueContext";
import { useDraftContext } from "../../context/DraftContext";
import { calculateAverage, getWeeklyPoints } from "../../utils/teamStats";
import { computeTeamProfileStats } from "../../utils/teamProfileStats";
import {
  SEASON_YEAR,
  isInstantAddOpen,
  FIRST_FREE_AGENCY_WEEK,
  LAST_BID_WEEK,
  SUPER_DOG_SPREAD,
  TOTAL_LINEUP_WEEKS,
  FIRST_PLAYOFF_WEEK,
} from "../../utils/season";
import { useBiddingWindow } from "../../hooks/useBiddingWindow";
import { fetchWeekGames } from "../../utils/weekGames";
import { useWatchlist } from "../../hooks/useWatchlist";
import TeamLogoImage from "./TeamLogoImage";
import InstantAddSheet from "../teams/InstantAddSheet";
import BidSheet from "../teams/BidSheet";
import {
  useSheetTransition,
  prefersReducedMotion,
  SHEET_ENTER_MS,
  SHEET_EXIT_MS,
} from "../sheets/useSheetTransition";
import { useSheetDrag } from "../sheets/useSheetDrag";
import { ModalPortal, useLockBodyScroll } from "../sheets/ModalPortal";
import { normalizeTeamName, teamLabel } from "../../utils/teamName";
import { isGameLive, formatPeriod, startOfEasternDay, LOCK_LEAD_MS, buildGamesBySlug, isTeamLocked } from "../../utils/gameStatus";

// Schedule rows carry live scores, which ESPN ingestion rewrites every 2
// minutes. The cache exists so reopening the same team is instant, not so the
// page can hold a schedule all session — an entry outliving one ingestion cycle
// shows a stale score with no way for the user to refresh short of a reload.
const SCHEDULE_TTL_MS = 60_000;
const _scheduleCache = new Map();

// Regular season sorts before postseason regardless of week numbering, since
// bowl/playoff rows restart their week count.
const SEASON_TYPE_ORDER = { regular: 0, postseason: 1 };

// Kickoffs are stored in UTC, and a west coast night game lands past midnight
// UTC, so counting days off the raw timestamp puts a Saturday game on Sunday.
// Both ends are reduced to an Eastern calendar date first, which is the same
// convention resolveWeek uses in backend/schedule.js.
//
// en-CA formats as YYYY-MM-DD, which parses back as a plain date.
const EASTERN_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/New_York",
  year: "numeric", month: "2-digit", day: "2-digit",
});

// Postseason weeks restart at 1, so a bowl row must never be read as week 0.
function isWeekZero(game) {
  return parseInt(game?.week, 10) === 0 && game?.season_type !== "postseason";
}

function daysUntilKickoff(dateStr) {
  if (!dateStr) return null;
  const kickoff = new Date(dateStr);
  if (Number.isNaN(kickoff.getTime())) return null;
  const kickoffDay = Date.parse(`${EASTERN_DAY.format(kickoff)}T00:00:00Z`);
  const today = Date.parse(`${EASTERN_DAY.format(new Date())}T00:00:00Z`);
  return Math.round((kickoffDay - today) / 86400000);
}

// Null while a game is in progress or already played, so the caller shows the
// date alone rather than counting down to something that has started.
function countdownLabel(dateStr) {
  const days = daysUntilKickoff(dateStr);
  if (days == null || days < 0) return null;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}

async function fetchSchedule(teamSlug) {
  const cacheKey = `${SEASON_YEAR}:${teamSlug}`;
  const cached = _scheduleCache.get(cacheKey);
  if (cached && Date.now() - cached.at < SCHEDULE_TTL_MS) return cached.data;
  const { data, error } = await supabase
    .from("games")
    .select("*")
    .eq("year", SEASON_YEAR)
    .or(`home_team.eq.${teamSlug},away_team.eq.${teamSlug}`);
  if (error) { console.error("Schedule fetch error:", error); return []; }
  // Week 0 stays in the fixture list. It is real football: it counts toward
  // the W-L and ATS records shown above this list, so hiding the game while
  // showing the record it produced would be incoherent. It is excluded from
  // fantasy scoring only, and the full schedule labels it "Wk 0" so it can
  // never be mistaken for a week 1 fixture.
  const sorted = (data || []).sort((a, b) => {
    const st = (SEASON_TYPE_ORDER[a.season_type] ?? 0) - (SEASON_TYPE_ORDER[b.season_type] ?? 0);
    if (st !== 0) return st;
    return (parseInt(a.week) || 0) - (parseInt(b.week) || 0);
  });
  _scheduleCache.set(cacheKey, { data: sorted, at: Date.now() });
  return sorted;
}

// onDraft is supplied by the draft room so a manager can inspect a team's
// stats and fixtures and then draft it without closing the drawer. Everywhere
// else the drawer is read-only pre-draft, so the prop is simply absent.
//
// onSelectTeam lets a fixture's opponent reopen the same drawer on that
// opponent instead: every caller already holds the open team in a single
// `drawerTeam` state value with a setter, so passing that setter through
// switches the drawer's subject in place rather than needing a stack or a
// second modal layered on top.
export default function TeamDrawer({ teamName, onClose, onDraft = null, draftBusy = false, onSelectTeam = null }) {
  const { teams: allTeams, currentUser, seasonConfig } = useAuth();
  const { leagueId, isDraftComplete, currentWeek, members, currentMemberId, leagueData, isDemo } = useLeague();
  const isTestLeague = Boolean(leagueData?.is_test);
  const { pickedTeamIds, draft } = useDraftContext();

  const [schedule, setSchedule] = useState([]);
  const [ownershipInfo, setOwnershipInfo] = useState(null);
  const [userTeams, setUserTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [watchError, setWatchError] = useState("");

  // The drawer opens on `teamName` alone, which the caller sets to null to
  // close. That leaves nothing to animate out, so the subject is latched here
  // and the drawer keeps rendering the team it was showing until the slide
  // down has finished. Fetched state is deliberately not cleared on close for
  // the same reason: the exiting drawer would otherwise flip to a loading
  // skeleton on its way off screen.
  const open = Boolean(teamName);
  const { mounted, entered } = useSheetTransition(open);
  const [lastTeamName, setLastTeamName] = useState(teamName);
  // A "watchlist is full" message belongs to the team it was raised on, not to
  // the next team the user opens.
  useEffect(() => {
    if (teamName) { setLastTeamName(teamName); setWatchError(""); }
  }, [teamName]);
  const activeName = teamName || lastTeamName;

  // The drawer had no keyboard dismiss, unlike every sheet built on
  // BottomSheet. On desktop that left the X button as the only way out that
  // did not involve aiming at the backdrop.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // The slug being acquired, which opens InstantAddSheet or BidSheet. The
  // drawer no longer runs an add or a swap of its own: see the comment on
  // canAcquire below.
  const [acquireTarget, setAcquireTarget] = useState(null);

  const watchlist = useWatchlist(currentMemberId, leagueId);

  const team = activeName ? allTeams[normalizeTeamName(activeName)] : null;
  const teamSlug = activeName ? (team?.id || normalizeTeamName(activeName)) : null;

  // Back to Overview whenever the subject changes, including an opponent
  // opened from a fixture row, so a new team never opens on its stats.
  const [tab, setTab] = useState("overview");
  useEffect(() => { setTab("overview"); }, [teamSlug]);
  // FCS opponents are in `teams` and open a drawer the same as an FBS team
  // (see the schedule below), but they were never in the draft pool and can
  // never be drafted, bid on or added: place_bid and instant_add_team both
  // require classification = 'fbs' server side. True while `team` has not
  // loaded yet, so this never flashes "not a fantasy team" for an FBS team on
  // a slow connection; false only once the team is known and is not FBS.
  const isFbs = !team || team.classification === "fbs";

  // Lock body scroll when open. Released on `open`, not on `mounted`: the page
  // behind should be usable again the moment the drawer is dismissed, not a
  // quarter of a second later when it finishes sliding.
  useLockBodyScroll(open);

  // weekly_lineups.week is `integer NOT NULL`, but config.season.currentWeek is
  // "Off-Season" outside the season and "Preseason" before week 1. Sending
  // either straight into .eq("week", ...) is a 400 from PostgREST, and sending
  // it into an upsert fails the same way, so add and swap were broken in the
  // off-season rather than merely noisy.
  //
  // Returns null when there is no lineup week. There is no sensible fallback:
  // defaulting to 1 would read and write a real week's roster while the app is
  // telling the user the season has not started.
  const lineupWeek = useMemo(() => {
    const raw = seasonConfig?.currentWeek ?? currentWeek;
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
  }, [seasonConfig?.currentWeek, currentWeek]);

  // Which week's lineup rows describe who owns this team right now.
  //
  // Not the same question as which week may be written. compile_draft_rosters
  // writes week 1 the moment a draft completes, so every drafted roster exists
  // well before config.season.currentWeek stops saying "Preseason". Reading
  // ownership at lineupWeek meant it was null all preseason and fetchLineups
  // returned early, so a team sitting in the viewer's own lineup was labelled
  // "Free Agent" with an Add Team button next to it.
  //
  // Writes still gate on lineupWeek. Reading a roster that exists is safe;
  // writing to a week the app says has not started is not.
  const rosterWeek = lineupWeek ?? 1;

  // Whether a team may be acquired at all right now, computed exactly as
  // TeamsTab computes it: isInstantAddOpen owns the rule so the drawer and the
  // board can never disagree about whether a team can be added.
  //
  // Inside the instant add window each team closes at its own lineup lock
  // (migration 076), and so does dropping one, so the drawer needs this week's
  // games. fetchWeekGames is the cached reader the Teams tab already filled,
  // so this rarely costs a request.
  const [weekGamesBySlug, setWeekGamesBySlug] = useState({});
  useEffect(() => {
    if (!lineupWeek) return undefined;
    let cancelled = false;
    fetchWeekGames(lineupWeek).then((rows) => {
      if (!cancelled) setWeekGamesBySlug(buildGamesBySlug(rows));
    });
    return () => { cancelled = true; };
  }, [lineupWeek]);

  const instantAddOpen = isInstantAddOpen({
    seasonConfig,
    draftCompletedAt: draft?.completed_at,
    isTestLeague,
  });
  const lockedForAdd = (slug) => instantAddOpen && isTeamLocked(weekGamesBySlug[slug], slug).locked;
  // Same window as TeamsTab.
  const biddingWindow = useBiddingWindow();
  const biddingOpen = biddingWindow.state === "open";
  const canAcquire = !isDemo && (instantAddOpen || biddingOpen) && isFbs;

  // Fetch incremental data when drawer opens
  useEffect(() => {
    // No clearing on close. run() resets all three before it fetches, so a
    // different team can never show the previous one's data, and leaving them
    // in place is what lets the closing drawer animate out still populated.
    if (!teamName || !leagueId) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setSchedule([]); setOwnershipInfo(null); setUserTeams([]);

      const [scheduleData, { ownership, roster }] = await Promise.all([
        fetchSchedule(teamSlug),
        fetchLineups(teamName, rosterWeek),
      ]);

      if (!cancelled) {
        setSchedule(scheduleData);
        setOwnershipInfo(ownership);
        setUserTeams(roster);
        setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
    // members is a dep because the drawer can open before LeagueContext has
    // resolved them, and ownership is unreadable until it has.
  }, [teamName, leagueId, members, rosterWeek]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-reads ownership after an instant add, so the banner flips from Free
  // Agent to owned in place. The schedule is untouched: an add cannot change
  // a fixture list, and refetching it would discard the cached copy.
  async function refreshOwnership() {
    const { ownership, roster } = await fetchLineups(teamName, rosterWeek);
    setOwnershipInfo(ownership);
    setUserTeams(roster);
  }

  // One read for the whole league's week, rather than one for ownership and a
  // second for the viewer's own roster — the viewer is a member like any other,
  // so their row is already in the result. Member identity and owner names come
  // from LeagueContext, which holds league_members joined with users and keeps
  // it live over a realtime subscription; re-querying it here produced an extra
  // round trip plus a per-owner users lookup, and could disagree with the names
  // shown elsewhere on the page.
  async function fetchLineups(schoolName, weekNum) {
    const empty = { ownership: null, roster: [] };
    if (!members?.length || !weekNum) return empty;

    const normalized = normalizeTeamName(schoolName);
    const { data: lineupRows } = await supabase
      .from("weekly_lineups").select("member_id, starters, bench, captain")
      .eq("league_id", leagueId).eq("week", weekNum)
      .in("member_id", members.map(m => m.id));

    const lineupByMember = {};
    (lineupRows || []).forEach(r => { lineupByMember[r.member_id] = r; });

    let ownership = null;
    for (const member of members) {
      const lineup = lineupByMember[member.id] || {};
      const starters = lineup.starters || [];
      const bench = lineup.bench || [];
      const status = starters.includes(normalized)
        ? "starting"
        : bench.includes(normalized) ? "bench" : null;
      if (!status) continue;

      const fullName = `${member.first_name || ""} ${member.last_name || ""}`.trim();
      ownership = {
        status,
        ownerName: fullName || member.team_name || "Unknown Owner",
        teamName: member.team_name || "Unnamed Team",
        isMine: member.id === currentMemberId,
        isCaptain: lineup.captain === normalized,
      };
      break;
    }

    const own = currentMemberId ? lineupByMember[currentMemberId] : null;
    const roster = own ? [...(own.starters || []), ...(own.bench || [])] : [];

    return { ownership, roster };
  }

  // ── Acquisition ───────────────────────────────────────────────────────────────

  // Acquisition is delegated, never done here.
  //
  // This used to upsert weekly_lineups directly, which took a free agent
  // instantly with no free agency window check: mid-season that bypassed
  // blind waiver bidding entirely, and it kept working after free agency
  // closed. It also wrote no move_history row, so the pickup never reached
  // the activity feed, and it refused in the preseason, the one time an
  // instant add is legitimate.
  //
  // TeamsTab already had this right, so the drawer now opens the same two
  // sheets it does: instant_add_team in the preseason, a blind bid in season.
  // The rule lives in one place, and the drawer is reachable from the Teams
  // tab, the draft room and the lineup, so it is the wrong place for a second
  // copy of it.
  function handleAddTeam() {
    if (!currentUser || !teamSlug || !canAcquire) return;
    setAcquireTarget(teamSlug);
  }

  // ── Watchlist ─────────────────────────────────────────────────────────────────

  const watched = watchlist.isWatched(teamSlug);

  async function handleToggleWatch() {
    setWatchError("");
    const reason = await watchlist.toggle(teamSlug);
    if (reason) setWatchError(reason);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  // Why a free agent cannot be taken right now.
  //
  // This slot used to be an empty string, so the drawer showed a team marked
  // "Free Agent" with nothing beside it and no way to tell whether free agency
  // had not opened yet, was between windows, or was over for the season. The
  // three have different answers and only one of them means "wait".
  function acquisitionBlockedReason() {
    if (isDemo) return "This is the demo league. Sign up to add teams in a league of your own.";
    switch (biddingWindow.state) {
      case "before":
        return `Free agency opens at 10am ET on the Sunday after week ${FIRST_FREE_AGENCY_WEEK}. Until then, rosters are as drafted.`;
      case "waiting":
        return `Bidding opens at 10am ET Sunday, after week ${biddingWindow.afterWeek}.`;
      case "resolving":
        return "Bidding has closed. Bids resolve at 6am ET.";
      case "closed":
        return `Bidding is closed for the season. The last window followed week ${LAST_BID_WEEK}.`;
      case "locked":
        return "Free agency is paused by the commissioner.";
      default:
        return "";
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  const profileStats = useMemo(
    () => computeTeamProfileStats({ slug: teamSlug, games: schedule, team, allTeams }),
    [teamSlug, schedule, team, allTeams]
  );

  if (!mounted || !activeName) return null;

  const cs = team?.currentSeason || {};
  const view = describeSchedule({ schedule, teamSlug, allTeams, cs, lineupWeek, preDraft: !isDraftComplete });

  // Averaged over weeks that actually scored. Week 0 is excluded from
  // game_points on the backend, and byes write nothing, so both would drag
  // the average down for no reason.
  const scoredWeeks = Object.keys(cs.weeklyPoints || {}).filter((w) => String(w) !== "0").length;
  const avgPts = calculateAverage(cs.gamePoints || 0, scoredWeeks);

  const status = describeStatus({
    loading,
    isDraftComplete,
    isDrafted: pickedTeamIds?.has(team?.id),
    ownershipInfo,
    isFbs,
  });

  const action = describeAction({
    isDraftComplete,
    isDrafted: pickedTeamIds?.has(team?.id),
    onDraft,
    ownershipInfo,
    loading,
    isFbs,
    canAcquire,
    instantAddOpen,
    teamLocked: lockedForAdd(teamSlug),
    signedIn: Boolean(currentUser),
    blockedReason: isDraftComplete && !canAcquire ? acquisitionBlockedReason() : "",
  });

  const openTeam = onSelectTeam ? (name) => onSelectTeam(name) : null;


  return (
    // Rendered into document.body, above the bottom nav (zIndex 50), so the
    // nav cannot be used to leave the page with the drawer still open, and
    // outside any transformed ancestor that would pin a fixed element inside a
    // scrolling card. 9000 rather than BottomSheet's 10000, so the bid and
    // instant-add sheets opened from here layer above it.
    <ModalPortal open={mounted}>
      <DrawerShell
        entered={entered}
        onClose={onClose}
        accent={team?.colors?.primary || team?.color}
        header={
          <DrawerHeader
            team={team}
            name={activeName}
            record={cs.record}
            status={status}
            watchable={Boolean(currentMemberId) && !isDemo && isFbs}
            watched={watched}
            watchDisabled={watchlist.slugs === null}
            onToggleWatch={handleToggleWatch}
            watchError={watchError}
          />
        }
        footer={action && (
          <ActionBar
            action={action}
            draftBusy={draftBusy}
            onDraft={() => onDraft(team.id)}
            onAcquire={handleAddTeam}
          />
        )}
      >
        {loading ? (
          <DrawerSkeleton />
        ) : (
          <>
            <DrawerTabs tab={tab} onChange={setTab} />

            {tab === "overview" && (
              <>
                <ThisWeek game={view.focus} label={view.focusLabel} onOpenTeam={openTeam} />

                {isDraftComplete && (
                  <FormStrip weeks={view.form} total={cs.gamePoints ?? 0} avg={avgPts} />
                )}

                {view.upcoming.length > 0 && (
                  <Section title={view.upcomingTitle}>
                    <div className="divide-y divide-hairline">
                      {view.upcoming.map((row) => (
                        <UpcomingRow key={row.key} row={row} onOpenTeam={openTeam} />
                      ))}
                    </div>
                  </Section>
                )}
              </>
            )}

            {tab === "stats" && <StatsPanel groups={profileStats} />}

            {tab === "schedule" && (
              <FullSeason rows={view.season} teamName={activeName} onOpenTeam={openTeam} defaultOpen />
            )}
          </>
        )}
      </DrawerShell>

      {/* Acquisition sheets. BottomSheet renders at zIndex 10000, above this
          drawer. onAdded re-reads ownership so the header flips from Free
          agent to owned without closing and reopening the drawer. */}
      {acquireTarget && instantAddOpen && (
        <InstantAddSheet
          leagueId={leagueId}
          teamSlug={acquireTarget}
          teamName={allTeams?.[acquireTarget]?.school || activeName}
          myRoster={userTeams}
          lockedSlugs={new Set(userTeams.filter(lockedForAdd))}
          allTeams={allTeams}
          onClose={() => setAcquireTarget(null)}
          onAdded={refreshOwnership}
        />
      )}

      {acquireTarget && !instantAddOpen && (
        <BidSheet
          leagueId={leagueId}
          memberId={currentMemberId}
          teamSlug={acquireTarget}
          teamName={allTeams?.[acquireTarget]?.school || activeName}
          myRoster={userTeams}
          allTeams={allTeams}
          onClose={() => setAcquireTarget(null)}
        />
      )}
    </ModalPortal>
  );
}

// ── Schedule model ────────────────────────────────────────────────────────────
//
// Everything the drawer shows about fixtures comes out of one pass over the
// schedule rows, so the focus card, the form strip, the next three and the
// full season can never disagree about which week a game belongs to.

const isComplete = (g) => Boolean(g?.game_complete || g?.gameComplete);

// games.home_spread is from the home team's side, so it flips for an away
// game. Negative means favoured, matching calculateTeamFantasyPoints.
function teamSpread(game, teamSlug) {
  if (typeof game.home_spread !== "number") return null;
  return game.home_team === teamSlug ? game.home_spread : -game.home_spread;
}

function spreadText(spread) {
  if (spread == null) return null;
  if (spread === 0) return "Pick'em";
  return spread < 0 ? `Favoured by ${Math.abs(spread)}` : `Underdog by ${spread}`;
}

function describeGame(game, teamSlug, allTeams, cs) {
  const isHome = game.home_team === teamSlug;
  const oppSlug = isHome ? game.away_team : game.home_team;
  const ts = isHome ? game.home_score : game.away_score;
  const os = isHome ? game.away_score : game.home_score;
  const complete = isComplete(game);
  const live = !complete && isGameLive({ gameStatus: game.game_status, period: game.period });
  const spread = teamSpread(game, teamSlug);
  const postseason = game.season_type === "postseason";
  const scoring = !postseason && !isWeekZero(game);
  const week = parseInt(game.week, 10);
  return {
    key: game.id || `${game.season_type}-${game.week}-${oppSlug}`,
    week,
    weekLabel: postseason ? "Bowl" : `Wk ${game.week}`,
    playoff: !postseason && isPlayoffWeek(week),
    postseason,
    weekZero: isWeekZero(game),
    prefix: isHome ? "vs" : "@",
    opponent: teamLabel(oppSlug, allTeams),
    // FCS opponents open the same drawer as any other team, but they were
    // never in the draft pool and can never be acquired, so the drawer has
    // nothing to offer for one beyond a name it already has right here. Not
    // clickable, rather than clickable into a drawer with no action on it.
    opponentIsFbs: allTeams?.[oppSlug]?.classification === "fbs",
    date: game.date || null,
    timeTbd: Boolean(game.start_time_tbd),
    spread,
    superDog: spread != null && spread >= SUPER_DOG_SPREAD,
    complete,
    live,
    period: live ? formatPeriod(game.period) : null,
    score: ts != null && os != null ? `${ts}-${os}` : null,
    liveScore: live ? `${ts ?? 0}-${os ?? 0}` : null,
    won: complete && ts != null && os != null ? ts > os : null,
    points: complete && scoring ? getWeeklyPoints(cs, game.week) : null,
  };
}

// Lineup weeks 11 to 13 are the fantasy playoffs, and lineup weeks share
// their numbers with games.week (see SEASON_WEEKS), so a regular season game
// in those weeks is a playoff game for whoever starts it.
function isPlayoffWeek(week) {
  return week >= FIRST_PLAYOFF_WEEK && week <= TOTAL_LINEUP_WEEKS;
}

function byeRow(week) {
  return { key: `bye-${week}`, week, weekLabel: `Wk ${week}`, bye: true, playoff: isPlayoffWeek(week) };
}

function describeSchedule({ schedule, teamSlug, allTeams, cs, lineupWeek, preDraft }) {
  const games = schedule.map((g) => describeGame(g, teamSlug, allTeams, cs));
  const regular = games.filter((g) => !g.postseason);
  const byWeek = new Map(regular.map((g) => [g.week, g]));

  // Before the draft, and in the off-season, there is no "this week": the
  // question is how the season opens, so the focus is the first unplayed game.
  const firstUnplayed = games.find((g) => !g.complete) || null;

  let focus;
  let focusLabel;
  if (preDraft || !lineupWeek) {
    focus = firstUnplayed;
    focusLabel = firstUnplayed ? "Next game" : "Season over";
  } else if (lineupWeek > TOTAL_LINEUP_WEEKS) {
    focus = firstUnplayed;
    focusLabel = "Next game";
  } else {
    focus = byWeek.get(lineupWeek) || { bye: true, week: lineupWeek };
    focusLabel = `Week ${lineupWeek}`;
  }

  // One entry per lineup week played so far, byes included, so a gap in the
  // strip reads as a bye rather than a missing bar.
  const lastWeek = Math.min(lineupWeek || 0, TOTAL_LINEUP_WEEKS);
  const form = [];
  for (let w = 1; w <= lastWeek; w += 1) {
    const g = byWeek.get(w);
    if (!g) form.push({ week: w, bye: true });
    else form.push({ week: w, points: g.points, live: g.live, pending: !g.complete });
  }

  // The weeks after the focus week, byes shown, because a bye is exactly what
  // a manager weighing a pickup needs to see coming.
  let upcoming = [];
  let upcomingTitle = "Coming up";
  if (preDraft || !lineupWeek || lineupWeek >= TOTAL_LINEUP_WEEKS) {
    upcoming = games.filter((g) => !g.complete && g !== focus).slice(0, 3);
    upcomingTitle = preDraft ? "Opening stretch" : "Coming up";
  } else {
    for (let w = lineupWeek + 1; w <= Math.min(lineupWeek + 3, TOTAL_LINEUP_WEEKS); w += 1) {
      upcoming.push(byWeek.get(w) || byeRow(w));
    }
  }

  // The full season lists byes too, in every lineup week without a game. A
  // bye in a playoff week is the single most important fixture fact about a
  // team, and a list of games alone hides it as a gap in the week numbers.
  const season = [];
  const lastRegular = Math.max(TOTAL_LINEUP_WEEKS, ...regular.map((g) => g.week));
  for (let w = 0; w <= lastRegular; w += 1) {
    const inWeek = regular.filter((g) => g.week === w);
    if (inWeek.length) season.push(...inWeek);
    else if (w >= 1 && w <= TOTAL_LINEUP_WEEKS) season.push(byeRow(w));
  }
  season.push(...games.filter((g) => g.postseason));

  return { focus, focusLabel, form, upcoming, upcomingTitle, season };
}

// ── Status and action ─────────────────────────────────────────────────────────

function describeStatus({ loading, isDraftComplete, isDrafted, ownershipInfo, isFbs }) {
  if (loading) return null;
  // Checked before the draft-complete branches: an FCS opponent was never in
  // the draft pool, so "Available in the draft" would be a real promise this
  // team cannot keep.
  if (!isFbs) return { tone: "muted", text: "FCS" };
  if (!isDraftComplete) {
    return isDrafted
      ? { tone: "muted", text: "Drafted" }
      : { tone: "brand", text: "Available in the draft" };
  }
  if (!ownershipInfo) return { tone: "success", text: "Free agent" };
  const { status, ownerName, teamName, isMine, isCaptain } = ownershipInfo;
  if (isMine) {
    if (isCaptain) return { tone: "captain", text: "Your captain" };
    return status === "starting"
      ? { tone: "brand", text: "In your starting lineup" }
      : { tone: "warning", text: "On your bench" };
  }
  return {
    tone: "muted",
    text: `${isCaptain ? "Captain for" : status === "starting" ? "Starting for" : "Benched by"} ${teamName || ownerName}`,
  };
}

// The one thing the viewer can do with this team right now, or null when
// there is nothing to do. The reason a free agent cannot be taken is an
// action too: it answers the question the missing button raises.
function describeAction({
  isDraftComplete, isDrafted, onDraft, ownershipInfo, loading,
  canAcquire, instantAddOpen, signedIn, blockedReason, isFbs, teamLocked = false,
}) {
  if (loading) return null;
  // canAcquire already folds isFbs in, so this only matters for the
  // pre-draft branch below: nothing to do with an FCS team, ever.
  if (!isFbs) return null;
  if (!isDraftComplete) {
    return onDraft && !isDrafted ? { kind: "draft", label: "Draft" } : null;
  }
  if (ownershipInfo || !signedIn) return null;
  // Free agency is open, but this team closed at its own lineup lock
  // (migration 076). Said as a note rather than a missing button, for the
  // same reason blockedReason is.
  if (canAcquire && instantAddOpen && teamLocked) {
    return { kind: "note", text: "This team's game has started. It can be added again once this week is over." };
  }
  if (canAcquire) {
    // In season it opens a blind bid, so the label must not promise an add.
    return { kind: "acquire", label: instantAddOpen ? "Add team" : "Place bid" };
  }
  return blockedReason ? { kind: "note", text: blockedReason } : null;
}

// ── Formatting ────────────────────────────────────────────────────────────────

function formatDay(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function formatTime(date) {
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).replace(":00", "");
}

// Same rule as the database lock trigger: 15 minutes before kickoff, or the
// start of gameday when the kickoff time is still TBD.
function lockText(game) {
  if (!game?.date) return null;
  const kickoff = new Date(game.date);
  if (Number.isNaN(kickoff.getTime())) return null;
  const lockAt = game.timeTbd ? startOfEasternDay(kickoff) : new Date(kickoff.getTime() - LOCK_LEAD_MS);
  if (Date.now() >= lockAt.getTime()) return "Locked";
  return game.timeTbd ? "Locks at the start of gameday" : `Locks ${formatTime(lockAt)}`;
}

// Compact line for a column: "-3.5", "+7", "PK".
function lineText(spread) {
  if (spread === 0) return "PK";
  return spread > 0 ? `+${spread}` : `${spread}`;
}

function pointsText(points) {
  if (points == null) return "";
  return points > 0 ? `+${points}` : `${points}`;
}

function pointsClass(points) {
  if (points == null || points === 0) return "text-ink-muted";
  return points > 0 ? "text-success-text" : "text-danger-text";
}

// ── Pieces ────────────────────────────────────────────────────────────────────

const TONE = {
  brand: "bg-brand-bg text-brand",
  success: "bg-success-bg text-success-text",
  warning: "bg-captain-fill text-captain-text",
  muted: "bg-surface-subtle text-ink-muted",
  captain: "bg-captain-fill text-captain-text",
};

function DrawerHeader({ team, name, record, status, watchable, watched, watchDisabled, onToggleWatch, watchError }) {
  return (
    <div>
      <div className="flex items-center gap-3 pr-20">
        <TeamLogoImage
          teamId={team?.id}
          teamName={name}
          primaryColor={team?.colors?.primary || team?.color}
          size={48}
        />
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-black text-ink leading-tight line-clamp-2 break-words">{name}</h2>
          <p className="text-sm text-ink-muted truncate">
            {[record, team?.conference || "Independent"].filter(Boolean).join(" · ")}
          </p>
        </div>
      </div>
      <div className="mt-3 min-h-[26px]">
        {status && (
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${TONE[status.tone]}`}>
            {status.text}
          </span>
        )}
      </div>
      {/* Star sits left of the close button, a full target apart. The two used
          to be 44px targets four pixels apart at 390px. */}
      {watchable && (
        <button
          type="button"
          onClick={onToggleWatch}
          disabled={watchDisabled}
          aria-pressed={watched}
          aria-label={watched ? "Remove from watchlist" : "Add to watchlist"}
          className="absolute top-3 right-14 p-2 rounded-full hover:bg-surface-subtle disabled:opacity-40 transition-colors"
        >
          <Star size={20} className={watched ? "fill-amber-400 text-captain" : "text-ink-faint"} />
        </button>
      )}
      {watchError && <p className="text-xs text-danger-text mt-2">{watchError}</p>}
    </div>
  );
}

// Three tabs rather than one long scroll. Overview is what the drawer showed
// before the stats arrived, so a manager who opens a team only to add it never
// scrolls past thirty numbers to reach the button.
const DRAWER_TABS = [
  { id: "overview", label: "Overview" },
  { id: "stats", label: "Stats" },
  { id: "schedule", label: "Schedule" },
];

function DrawerTabs({ tab, onChange }) {
  return (
    <div role="tablist" className="flex p-1 mb-4 rounded-xl bg-surface-subtle">
      {DRAWER_TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={tab === t.id}
          onClick={() => onChange(t.id)}
          className={`flex-1 rounded-lg text-[13px] font-semibold transition-colors ${
            tab === t.id ? "bg-surface-card text-ink shadow-sm" : "text-ink-muted"
          }`}
          style={{ minHeight: 34 }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// Numbers as tiles, not sentences: a figure, a label, and a comparison where
// one helps. Colour carries the verdict so the grid can be scanned without
// being read.
const TILE_TONE = { good: "text-success-text", bad: "text-danger-text" };

function StatsPanel({ groups }) {
  return (
    <>
      {groups.map((group) => (
        <Section key={group.title} title={group.title}>
          <div className="grid grid-cols-3 gap-2">
            {group.tiles.map((tile) => (
              <div key={tile.label} className="rounded-xl bg-surface-subtle border border-hairline px-2 py-2 min-w-0">
                <div className="text-[10px] font-semibold text-ink-muted truncate">{tile.label}</div>
                <div className={`text-lg font-extrabold leading-tight tabular-nums truncate ${TILE_TONE[tile.tone] || "text-ink"}`}>
                  {tile.value}
                </div>
                {tile.note && <div className="text-[10px] text-ink-faint truncate">{tile.note}</div>}
              </div>
            ))}
          </div>
        </Section>
      ))}
    </>
  );
}

function Section({ title, right = null, children }) {
  return (
    <section className="mb-5">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-[11px] font-bold text-ink-muted uppercase tracking-wide">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

// Prefix and name as two flex items, so a long name wraps under itself rather
// than leaving "vs" stranded alone on the first line.
function Opponent({ game, onOpenTeam, className = "" }) {
  return (
    <span className={`flex items-baseline gap-1 min-w-0 ${className}`}>
      <span className="text-ink-muted font-normal flex-shrink-0">{game.prefix}</span>
      {onOpenTeam && game.opponentIsFbs ? (
        <button
          type="button"
          onClick={() => onOpenTeam(game.opponent)}
          className="min-w-0 text-left underline decoration-dotted decoration-gray-300 underline-offset-4 hover:text-brand"
        >
          {game.opponent}
        </button>
      ) : <span className="min-w-0">{game.opponent}</span>}
    </span>
  );
}

// The card the drawer exists for: this week's game, in whichever of its three
// states it is in. Upcoming shows what decides a start (the line, the lock),
// live shows the score, final shows the points it earned.
function ThisWeek({ game, label, onOpenTeam }) {
  if (!game) {
    return (
      <Section title={label}>
        <div className="rounded-2xl bg-surface-subtle px-4 py-4 text-sm text-ink-muted">No games left this season.</div>
      </Section>
    );
  }

  if (game.bye) {
    return (
      <Section title={label}>
        <div className="rounded-2xl bg-surface-subtle px-4 py-4">
          <div className="text-lg font-bold text-ink">Bye week</div>
          <div className="text-sm text-ink-muted mt-0.5">No game, so a start here scores 0.</div>
        </div>
      </Section>
    );
  }

  const lock = !game.complete && !game.live ? lockText(game) : null;

  return (
    <Section title={label}>
      <div className="rounded-2xl bg-surface-subtle px-4 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Opponent game={game} onOpenTeam={onOpenTeam} className="text-lg font-bold text-ink" />
            {game.spread != null && !game.complete && (
              <div className="text-sm text-ink-muted mt-0.5">
                {spreadText(game.spread)}
                {game.superDog && (
                  <span className="ml-2 inline-flex px-1.5 py-0.5 rounded bg-tripplay-fill text-tripplay-text text-[11px] font-bold align-middle">
                    Super Dog
                  </span>
                )}
              </div>
            )}
          </div>

          {game.complete && game.points != null && (
            <div className="text-right flex-shrink-0">
              <div className={`text-2xl font-black leading-none ${pointsClass(game.points)}`}>{pointsText(game.points)}</div>
              <div className="text-[11px] text-ink-muted mt-1">pts</div>
            </div>
          )}
        </div>

        <div className="mt-3 text-sm">
          {game.live ? (
            <span className="inline-flex items-center gap-2 font-semibold text-success-text">
              <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
              Live {game.liveScore}{game.period ? ` · ${game.period}` : ""}
            </span>
          ) : game.complete ? (
            <span className="text-ink-muted">
              Final · <span className={game.won ? "text-success-text font-semibold" : "text-danger-text font-semibold"}>
                {game.won ? "W" : "L"} {game.score}
              </span>
            </span>
          ) : (
            <>
              <div className="text-ink font-medium">
                {formatDay(game.date)}
                {game.date && !game.timeTbd ? `, ${formatTime(new Date(game.date))}` : ", time TBD"}
                {countdownLabel(game.date) && <span className="text-ink-muted font-normal"> · {countdownLabel(game.date)}</span>}
              </div>
              {lock && <div className="text-xs text-ink-muted mt-0.5">Lineup {lock.charAt(0).toLowerCase() + lock.slice(1)}</div>}
            </>
          )}
        </div>

        {game.weekZero && <div className="text-xs text-ink-muted mt-2">Week 0 counts toward the record but scores no fantasy points.</div>}
      </div>
    </Section>
  );
}

// Fantasy points by week on the scoring scale, -8 to +13, so a bar's height
// means the same thing on every team. W-L alone hides a team that wins without
// covering, which scores little.
const FORM_MAX = 13;
const FORM_MIN = -8;

function FormStrip({ weeks, total, avg }) {
  if (!weeks.length) return null;
  const up = 40;
  const down = Math.round(up * (Math.abs(FORM_MIN) / FORM_MAX));
  return (
    <Section
      title="Form"
      right={<span className="text-xs text-ink-muted"><span className="font-semibold text-ink">{total}</span> pts · {avg} avg</span>}
    >
      <div className="flex gap-1.5">
        {weeks.map((w) => {
          const p = w.points;
          const scored = p != null;
          const h = scored ? Math.max(3, Math.round((Math.abs(p) / (p >= 0 ? FORM_MAX : Math.abs(FORM_MIN))) * (p >= 0 ? up : down))) : 0;
          return (
            <div key={w.week} className="flex-1 min-w-0 flex flex-col items-center">
              <div className="w-full flex flex-col justify-end items-center" style={{ height: up }}>
                {scored && p >= 0 && <div className={`w-full rounded-t ${p > 0 ? "bg-success" : "bg-hairline"}`} style={{ height: h }} />}
              </div>
              <div className="w-full h-px bg-hairline" />
              <div className="w-full flex flex-col items-center" style={{ height: down }}>
                {scored && p < 0 && <div className="w-full rounded-b bg-danger-fill" style={{ height: h }} />}
                {!scored && (
                  <span className="text-[9px] text-ink-faint mt-1">
                    {w.bye ? "BYE" : w.live ? "LIVE" : "·"}
                  </span>
                )}
              </div>
              <div className={`text-[10px] mt-1 font-semibold ${scored ? pointsClass(p) : "text-ink-faint"}`}>
                {scored ? pointsText(p) : ""}
              </div>
              <div className="text-[9px] text-ink-faint">{w.week}</div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function UpcomingRow({ row, onOpenTeam }) {
  if (row.bye) {
    return (
      <div className="flex items-center gap-3 py-2.5">
        <span className="w-12 text-xs font-semibold text-ink-faint">{row.weekLabel}</span>
        <span className={`text-sm font-semibold ${row.playoff ? "text-captain-text" : "text-ink-faint"}`}>
          {row.playoff ? "Bye in a playoff week" : "Bye"}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 py-2.5">
      <span className="w-12 text-xs font-semibold text-ink-muted flex-shrink-0">{row.weekLabel}</span>
      <div className="flex-1 min-w-0 text-sm font-semibold text-ink leading-snug">
        <Opponent game={row} onOpenTeam={onOpenTeam} />
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {row.playoff && (
          <span className="px-1.5 py-0.5 rounded bg-brand-bg text-brand text-[10px] font-bold">Playoffs</span>
        )}
        {row.superDog && (
          <span className="px-1.5 py-0.5 rounded bg-tripplay-fill text-tripplay-text text-[10px] font-bold">Super Dog</span>
        )}
        <span className="text-sm text-ink-muted tabular-nums">
          {row.spread == null ? <span className="text-ink-faint text-xs">No line</span> : lineText(row.spread)}
        </span>
      </div>
    </div>
  );
}

// The whole fixture list, collapsed by default: it is reference, not the
// reason the drawer was opened. Played games lead with points, not the score.
function FullSeason({ rows, teamName, onOpenTeam, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!rows.length) return null;

  // Grouped rather than tinted row by row, so the playoff weeks read as one
  // block with its own heading, the way the rules page presents them.
  const firstPlayoff = rows.findIndex((r) => r.playoff);
  const lastPlayoff = rows.length - 1 - [...rows].reverse().findIndex((r) => r.playoff);
  const groups = firstPlayoff === -1
    ? [{ rows }]
    : [
        { rows: rows.slice(0, firstPlayoff) },
        { rows: rows.slice(firstPlayoff, lastPlayoff + 1), playoff: true },
        { rows: rows.slice(lastPlayoff + 1) },
      ].filter((grp) => grp.rows.length);
  const playoffByes = rows.filter((r) => r.playoff && r.bye).length;

  return (
    <section className="mb-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between py-2 text-[11px] font-bold text-ink-muted uppercase tracking-wide"
      >
        Full season · {rows.filter((r) => !r.bye).length} games
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          {groups.map((grp, i) => grp.playoff ? (
            <div key={i} className="my-2 rounded-xl bg-brand-bg border border-hairline px-2 pb-1">
              <div className="flex items-baseline justify-between px-1 pt-2 pb-1">
                <span className="text-[11px] font-bold text-brand uppercase tracking-wide">Fantasy playoffs</span>
                <span className="text-[11px] text-ink-muted">
                  Weeks {FIRST_PLAYOFF_WEEK} to {TOTAL_LINEUP_WEEKS}
                  {playoffByes > 0 && <span className="text-captain-text font-semibold"> · {playoffByes === 1 ? "1 bye" : `${playoffByes} byes`}</span>}
                </span>
              </div>
              <div className="divide-y divide-hairline">
                {grp.rows.map((g) => <SeasonRow key={g.key} g={g} onOpenTeam={onOpenTeam} />)}
              </div>
            </div>
          ) : (
            <div key={i} className="divide-y divide-hairline px-2">
              {grp.rows.map((g) => <SeasonRow key={g.key} g={g} onOpenTeam={onOpenTeam} />)}
            </div>
          ))}
          <p className="text-[11px] text-ink-muted mt-2">
            Lines are from {teamName}'s side: negative means favoured. Played games show the closing line, and a dash means no line is posted yet. Week 0 and bowl games score no fantasy points.
          </p>
        </>
      )}
    </section>
  );
}

function SeasonRow({ g, onOpenTeam }) {
  if (g.bye) {
    return (
      <div className="flex items-center gap-2 py-2.5">
        <div className="w-11 flex-shrink-0 text-xs font-semibold text-ink-faint">{g.weekLabel}</div>
        {/* A playoff bye scores 0 in a week that decides the title, so it is
            called out rather than greyed like any other bye. */}
        <div className={`text-sm font-semibold ${g.playoff ? "text-captain-text" : "text-ink-faint"}`}>
          {g.playoff ? "Bye in a playoff week" : "Bye"}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 py-2.5">
      <div className="w-11 flex-shrink-0">
        <div className="text-xs font-semibold text-ink-muted">{g.weekLabel}</div>
        <div className="text-[10px] text-ink-faint">{g.date ? formatDay(g.date).replace(/^\w+, /, "") : ""}</div>
      </div>
      <div className="flex-1 min-w-0 text-sm font-medium text-ink leading-snug">
        <Opponent game={g} onOpenTeam={onOpenTeam} />
      </div>
      {/* The line on every row, played or not. For a played game it is the
          closing line, which is what the points were scored against, so it
          explains a win worth 0 or a loss worth +4. */}
      <div className="w-10 flex-shrink-0 text-right text-sm text-ink-muted tabular-nums">
        {g.spread == null ? <span className="text-ink-faint">—</span> : lineText(g.spread)}
      </div>
      <div className="w-14 flex-shrink-0 text-right">
        {g.complete ? (
          <>
            <div className={`text-sm font-bold ${g.points != null ? pointsClass(g.points) : "text-ink-faint"}`}>
              {g.points != null ? `${pointsText(g.points)} pts` : "No pts"}
            </div>
            <div className="text-[10px] text-ink-muted">{g.won ? "W" : "L"} {g.score}</div>
          </>
        ) : g.live ? (
          <div className="text-xs font-semibold text-success-text">Live {g.liveScore}</div>
        ) : null}
      </div>
    </div>
  );
}

function ActionBar({ action, draftBusy, onDraft, onAcquire }) {
  if (action.kind === "note") {
    return <p className="text-xs text-ink-muted text-center">{action.text}</p>;
  }
  const busy = action.kind === "draft" && draftBusy;
  return (
    <button
      type="button"
      onClick={action.kind === "draft" ? onDraft : onAcquire}
      disabled={busy}
      className="w-full py-3 rounded-xl bg-brand text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-1.5"
    >
      {action.kind === "acquire" && <Plus size={16} />}
      {busy ? "Drafting…" : action.label}
    </button>
  );
}

function DrawerSkeleton() {
  return (
    <div className="space-y-4">
      <div className="h-28 rounded-2xl bg-surface-subtle animate-pulse" />
      <div className="h-20 rounded-2xl bg-surface-subtle animate-pulse" />
      <div className="h-24 rounded-2xl bg-surface-subtle animate-pulse" />
    </div>
  );
}

// The sheet chrome: backdrop, slide, drag-to-dismiss.
//
// A separate component so the drag offset lives in state the drawer body does
// not own. When this sat in TeamDrawer, every touchmove re-rendered the whole
// drawer and the sheet stuttered behind the finger. Here the body arrives as
// already-built `children`, so React skips it on a drag frame and only the
// transform changes.
function DrawerShell({ entered, onClose, accent, header, footer, children }) {
  const { dragY, dragging, dragHandlers } = useSheetDrag(onClose);
  const reduceMotion = prefersReducedMotion();
  const duration = reduceMotion ? 0 : (entered ? SHEET_ENTER_MS : SHEET_EXIT_MS);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9000, pointerEvents: entered ? "auto" : "none" }}>
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        style={{
          // A drag on the backdrop is not a scroll of anything.
          touchAction: "none",
          // Fades with the drag, floored because this is also what blocks taps.
          opacity: entered ? Math.max(0.2, 1 - dragY / 400) : 0,
          transition: dragging ? "none" : `opacity ${duration}ms ease`,
        }}
      />

      <div
        className="absolute bottom-0 left-0 right-0 bg-surface-card rounded-t-3xl flex flex-col overflow-hidden"
        style={{
          // Taller than the old 60%: the drawer now answers the question it was
          // opened for, so it should not need scrolling to do it.
          maxHeight: "88vh",
          transform: entered ? `translateY(${dragY}px)` : "translateY(100%)",
          transition: dragging ? "none" : `transform ${duration}ms cubic-bezier(0.32, 0.72, 0, 1)`,
          willChange: "transform",
          boxShadow: "0 -8px 32px rgba(0,0,0,0.18)",
        }}
      >
        {/* A strip of the team's own colour: identity at a glance, without
            putting brand colours behind text that has to stay readable. */}
        {accent && <div className="h-1 flex-shrink-0" style={{ backgroundColor: accent }} />}

        {/* Handle and header together are the grab area. */}
        <div {...dragHandlers} className="flex-shrink-0 cursor-grab relative" style={{ touchAction: "none" }}>
          <div className="flex justify-center pt-2.5 pb-2">
            <div className="w-10 h-1 rounded-full bg-hairline" />
          </div>
          <button onClick={onClose} aria-label="Close"
            className="absolute top-3 right-4 p-2 hover:bg-surface-subtle rounded-full transition-colors">
            <X size={20} className="text-ink-faint" />
          </button>
          <div className="px-5 pb-4 border-b border-hairline">{header}</div>
        </div>

        <div
          className="overflow-y-auto flex-1 px-5 pt-4"
          // contain: reaching the end of the list must not hand the scroll on
          // to the page behind, which is locked but can still rubber-band.
          style={{ overscrollBehavior: "contain", paddingBottom: footer ? 16 : "calc(16px + env(safe-area-inset-bottom))" }}
        >
          {children}
        </div>

        {footer && (
          <div
            className="flex-shrink-0 px-5 pt-3 border-t border-hairline bg-surface-card"
            style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
